const linkedVariantAvailabilityCache = new Map();
const sectionHtmlCache = new Map();
const variantGridStateCache = new Map();
const countMetadataAttributes = [
  'data-count-mode',
  'data-visible-count',
  'data-total-count',
  'data-template-results',
  'data-template-simple',
  'data-template-range',
  'data-terms',
];
const VARIANT_GRID_CACHE_VERSION = 'v3';
const VARIANT_GRID_SESSION_PREFIX = `interval-variant-grid:${VARIANT_GRID_CACHE_VERSION}:`;
const paginationCaretIcon =
  '<span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>';

function hasVariantOptionFilter(search = window.location.search) {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  return Array.from(params.keys()).some((key) => key.startsWith('filter.v.option.'));
}

function isVariantAwareGridContext(container) {
  const context = container?.dataset?.gridContext;
  return context === 'collection' || context === 'search';
}

function shouldUseVariantAwarePagination(container, search = window.location.search) {
  return Boolean(isVariantAwareGridContext(container) && hasVariantOptionFilter(search));
}

function normalizeSearchValue(value) {
  return `${value ?? ''}`
    .trim()
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/\s+/g, ' ');
}

function normalizeSizeValue(value) {
  return normalizeSearchValue(value).replace(/^(uk|us|eu)\s+/, '');
}

function normalizeGenderValue(value) {
  const normalized = normalizeSearchValue(value);

  if (normalized === 'mens' || normalized === 'men s' || normalized === 'man' || normalized === 'male') {
    return 'men';
  }

  if (normalized === 'womens' || normalized === 'women s' || normalized === 'woman' || normalized === 'female') {
    return 'women';
  }

  return normalized;
}

function parseHtmlDocument(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

function getCanonicalOrigin() {
  try {
    const canonicalHref = document.querySelector('link[rel="canonical"]')?.href;
    return canonicalHref ? new URL(canonicalHref, window.location.origin).origin : window.location.origin;
  } catch (error) {
    return window.location.origin;
  }
}

function getProductDataOrigin() {
  const hostname = window.location.hostname;
  const isLocalPreview =
    hostname === '127.0.0.1' || hostname === 'localhost' || hostname === '[::1]' || hostname === '::1';

  return isLocalPreview ? getCanonicalOrigin() : window.location.origin;
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = `${value ?? ''}`;
  return div.innerHTML;
}

function createElementFromHtml(html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.firstElementChild;
}

function getRequestedPage(search = window.location.search) {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const page = Number.parseInt(params.get('page') || '1', 10);

  return Number.isFinite(page) && page > 0 ? page : 1;
}

function buildStorefrontUrl(pathname, searchParams) {
  return searchParams ? `${pathname}?${searchParams}` : pathname;
}

function buildPageSearchParams(searchParams, page) {
  const params = new URLSearchParams(searchParams);

  if (page > 1) {
    params.set('page', `${page}`);
  } else {
    params.delete('page');
  }

  return FacetFiltersForm.normalizeSearchParams(params.toString());
}

function buildPaginationUrl(pathname, searchParams, page) {
  return buildStorefrontUrl(pathname, buildPageSearchParams(searchParams, page));
}

function buildSectionUrl(pathname, sectionId, searchParams, page) {
  const params = new URLSearchParams(buildPageSearchParams(searchParams, page));
  params.set('section_id', sectionId);

  return `${pathname}?${params.toString()}`;
}

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function requestSectionHtml(url, attempt = 0) {
  const response = await fetch(url);

  if (!response.ok) {
    if (attempt < 4) {
      const retryDelay = response.status === 429 ? 600 * (attempt + 1) : 250 * (attempt + 1);
      await delay(retryDelay);
      return requestSectionHtml(url, attempt + 1);
    }

    throw new Error(`Failed to load section HTML for ${url}`);
  }

  return response.text();
}

async function requestProductData(handle, attempt = 0) {
  try {
    const response = await fetch(`${getProductDataOrigin()}/products/${handle}.js`);

    if (!response.ok) {
      throw new Error(`Invalid product response for ${handle}`);
    }

    return await response.json();
  } catch (error) {
    if (attempt < 2) {
      await delay(200 * (attempt + 1));
      return requestProductData(handle, attempt + 1);
    }

    return null;
  }
}

function fetchProductDataForHandle(handle) {
  if (!linkedVariantAvailabilityCache.has(handle)) {
    const request = requestProductData(handle).then((productData) => {
      if (productData == null) {
        linkedVariantAvailabilityCache.delete(handle);
      }

      return productData;
    });

    linkedVariantAvailabilityCache.set(handle, request);
  }

  return linkedVariantAvailabilityCache.get(handle);
}

function fetchSectionHtml(url) {
  if (!sectionHtmlCache.has(url)) {
    const request = requestSectionHtml(url).catch((error) => {
      sectionHtmlCache.delete(url);
      throw error;
    });

    sectionHtmlCache.set(url, request);
  }

  return sectionHtmlCache.get(url);
}

function extractCountMetadata(sourceDocument) {
  const source =
    sourceDocument.getElementById('ProductCount') || sourceDocument.getElementById('ProductCountDesktop');

  if (!source) return null;

  return {
    mode: source.dataset.countMode || 'simple',
    terms: source.dataset.terms || '',
    totalCount: Number.parseInt(source.dataset.totalCount || source.dataset.visibleCount || '0', 10) || 0,
    templateResults: source.dataset.templateResults || '',
    templateSimple: source.dataset.templateSimple || '',
    templateRange: source.dataset.templateRange || '',
  };
}

function applyCountMetadata(target, metadata, visibleCount) {
  if (!target || !metadata) return;

  target.dataset.countMode = metadata.mode;
  target.dataset.visibleCount = `${visibleCount}`;
  target.dataset.totalCount = `${metadata.totalCount}`;
  target.dataset.templateResults = metadata.templateResults;
  target.dataset.templateSimple = metadata.templateSimple;
  target.dataset.templateRange = metadata.templateRange;

  if (metadata.terms) {
    target.dataset.terms = metadata.terms;
  } else {
    target.removeAttribute('data-terms');
  }
}

function buildCountText(metadata, visibleCount) {
  if (!metadata) return '';

  if (metadata.mode === 'results') {
    return metadata.templateResults
      .replace('__TERMS__', escapeHtml(metadata.terms))
      .replace('__COUNT__', escapeHtml(visibleCount));
  }

  if (metadata.mode === 'range') {
    return metadata.templateRange
      .replace('__PRODUCT_COUNT__', escapeHtml(visibleCount))
      .replace('__TOTAL_COUNT__', escapeHtml(metadata.totalCount));
  }

  return metadata.templateSimple.replace('__COUNT__', escapeHtml(visibleCount));
}

function buildCorrectedCountMetadata(metadata, visibleCount) {
  if (!metadata) return metadata;

  if (metadata.mode === 'range') {
    return {
      ...metadata,
      mode: 'simple',
      totalCount: visibleCount,
    };
  }

  return metadata;
}

function getVisibleGridItems(root) {
  return Array.from(root.querySelectorAll('.grid__item'));
}

function getLinkedVariantInfo(item) {
  const productLink = item.querySelector('a[href*="/products/"][href*="variant="]');

  if (!productLink) return null;

  try {
    const linkedUrl = new URL(productLink.getAttribute('href'), window.location.origin);
    const linkedVariantId = linkedUrl.searchParams.get('variant');
    const handleMatch = linkedUrl.pathname.match(/^\/products\/([^/]+)/);

    if (!linkedVariantId || !handleMatch) return null;

    return {
      handle: handleMatch[1],
      variantId: linkedVariantId,
    };
  } catch (error) {
    return null;
  }
}

async function isGridItemVisible(item) {
  const linkedVariant = getLinkedVariantInfo(item);

  if (!linkedVariant) return true;

  const productData = await fetchProductDataForHandle(linkedVariant.handle);
  const matchedVariant = productData?.variants?.find((variant) => `${variant.id}` === linkedVariant.variantId);

  return !matchedVariant || matchedVariant.available !== false;
}

function buildPaginationParts(totalPages, currentPage) {
  if (totalPages <= 1) return [];

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
  }

  if (currentPage >= totalPages - 2) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
  }

  const sortedPages = Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const parts = [];

  sortedPages.forEach((page, index) => {
    const previousPage = sortedPages[index - 1];

    if (previousPage && page - previousPage > 1) {
      parts.push({ ellipsis: true, key: `ellipsis-${previousPage}-${page}` });
    }

    parts.push({ page, current: page === currentPage, key: `page-${page}` });
  });

  return parts;
}

function buildVirtualPaginationMarkup(totalPages, currentPage, pathname, searchParams) {
  if (totalPages <= 1) return '';

  const parts = buildPaginationParts(totalPages, currentPage);
  const previousPage = currentPage - 1;
  const nextPage = currentPage + 1;

  return `
    <div class="pagination-wrapper" data-page="${currentPage}">
      <nav class="pagination" role="navigation" aria-label="Pagination">
        <ul class="pagination__list list-unstyled" role="list">
          ${
            previousPage >= 1
              ? `
                <li>
                  <a
                    href="${buildPaginationUrl(pathname, searchParams, previousPage)}"
                    class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce"
                    aria-label="Previous page"
                  >
                    ${paginationCaretIcon}
                  </a>
                </li>
              `
              : ''
          }
          ${parts
            .map((part) => {
              if (part.ellipsis) {
                return `<li><span class="pagination__item">…</span></li>`;
              }

              if (part.current) {
                return `
                  <li>
                    <a
                      role="link"
                      aria-disabled="true"
                      class="pagination__item pagination__item--current light"
                      aria-current="page"
                      aria-label="Page ${part.page}"
                    >
                      ${part.page}
                    </a>
                  </li>
                `;
              }

              return `
                <li>
                  <a
                    href="${buildPaginationUrl(pathname, searchParams, part.page)}"
                    class="pagination__item link"
                    aria-label="Page ${part.page}"
                  >
                    ${part.page}
                  </a>
                </li>
              `;
            })
            .join('')}
          ${
            nextPage <= totalPages
              ? `
                <li>
                  <a
                    href="${buildPaginationUrl(pathname, searchParams, nextPage)}"
                    class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce"
                    aria-label="Next page"
                  >
                    ${paginationCaretIcon}
                  </a>
                </li>
              `
              : ''
          }
        </ul>
      </nav>
    </div>
  `;
}

function animateVirtualGrid(list) {
  if (!list) return;

  list.style.opacity = '0';
  list.style.transform = 'translateY(0.8rem)';
  list.style.transition = 'opacity 180ms ease, transform 180ms ease';

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      list.style.opacity = '1';
      list.style.transform = 'translateY(0)';
    });
  });

  list.addEventListener(
    'transitionend',
    () => {
      list.style.removeProperty('opacity');
      list.style.removeProperty('transform');
      list.style.removeProperty('transition');
    },
    { once: true }
  );
}

function getVariantGridBaseSearchParams(searchParams = window.location.search) {
  const rawSearchParams =
    typeof searchParams === 'string' && searchParams.startsWith('?') ? searchParams.slice(1) : searchParams;
  const params = new URLSearchParams(FacetFiltersForm.normalizeSearchParams(rawSearchParams || ''));

  params.delete('page');

  return FacetFiltersForm.normalizeSearchParams(params.toString());
}

function buildVariantGridCacheKey(container, searchParams = FacetFiltersForm.searchParamsPrev) {
  const context = container?.dataset?.gridContext || 'collection';
  return `${context}:${window.location.pathname}?${getVariantGridBaseSearchParams(searchParams)}`;
}

function buildVariantGridSessionKey(cacheKey) {
  return `${VARIANT_GRID_SESSION_PREFIX}${cacheKey}`;
}

function deserializeVariantGridState(snapshot) {
  if (!snapshot || snapshot.version !== VARIANT_GRID_CACHE_VERSION) return null;

  return {
    cacheKey: snapshot.cacheKey,
    context: snapshot.context,
    pathname: snapshot.pathname,
    baseSearchParams: snapshot.baseSearchParams,
    pageSize: snapshot.pageSize,
    sectionId: snapshot.sectionId,
    totalRawPages: snapshot.totalRawPages,
    nextRawPage: snapshot.nextRawPage || 1,
    visibleRecords: Array.isArray(snapshot.visibleRecords) ? snapshot.visibleRecords : [],
    fullyHydrated: Boolean(snapshot.fullyHydrated),
    waiters: [],
    sourceDocuments: new Map(),
    pendingVisibleCount: 0,
    hydrateToEnd: false,
    hydrationPromise: null,
    error: null,
    facetDefinitions: new Map(),
  };
}

function serializeVariantGridState(state) {
  return {
    version: VARIANT_GRID_CACHE_VERSION,
    cacheKey: state.cacheKey,
    context: state.context,
    pathname: state.pathname,
    baseSearchParams: state.baseSearchParams,
    pageSize: state.pageSize,
    sectionId: state.sectionId,
    totalRawPages: state.totalRawPages,
    nextRawPage: state.nextRawPage,
    visibleRecords: state.visibleRecords,
    fullyHydrated: state.fullyHydrated,
  };
}

function persistVariantGridState(state) {
  variantGridStateCache.set(state.cacheKey, state);

  try {
    window.sessionStorage.setItem(buildVariantGridSessionKey(state.cacheKey), JSON.stringify(serializeVariantGridState(state)));
  } catch (error) {
    // Ignore storage quota/session availability issues.
  }
}

function loadVariantGridStateFromSession(cacheKey) {
  try {
    const stored = window.sessionStorage.getItem(buildVariantGridSessionKey(cacheKey));

    if (!stored) return null;

    return deserializeVariantGridState(JSON.parse(stored));
  } catch (error) {
    return null;
  }
}

function createVariantGridState(container, searchParams) {
  return {
    cacheKey: buildVariantGridCacheKey(container, searchParams),
    context: container?.dataset?.gridContext || 'collection',
    pathname: window.location.pathname,
    baseSearchParams: getVariantGridBaseSearchParams(searchParams),
    pageSize: Number.parseInt(container?.dataset?.pageSize || '16', 10) || 16,
    sectionId: container?.dataset?.sectionId || document.getElementById('product-grid')?.dataset?.id || '',
    totalRawPages: Number.parseInt(container?.dataset?.totalPages || '1', 10) || 1,
    nextRawPage: 1,
    visibleRecords: [],
    fullyHydrated: false,
    waiters: [],
    sourceDocuments: new Map(),
    pendingVisibleCount: 0,
    hydrateToEnd: false,
    hydrationPromise: null,
    error: null,
    facetDefinitions: new Map(),
  };
}

function syncVariantGridState(state, container, searchParams) {
  state.context = container?.dataset?.gridContext || state.context;
  state.pathname = window.location.pathname;
  state.baseSearchParams = getVariantGridBaseSearchParams(searchParams);
  state.pageSize = Number.parseInt(container?.dataset?.pageSize || `${state.pageSize}`, 10) || state.pageSize || 16;
  state.sectionId = container?.dataset?.sectionId || state.sectionId;
  state.totalRawPages = Number.parseInt(container?.dataset?.totalPages || `${state.totalRawPages || 1}`, 10) || 1;
  state.cacheKey = buildVariantGridCacheKey(container, searchParams);
}

function getVariantGridState(container, searchParams) {
  const cacheKey = buildVariantGridCacheKey(container, searchParams);
  let state = variantGridStateCache.get(cacheKey);

  if (!state) {
    state = loadVariantGridStateFromSession(cacheKey) || createVariantGridState(container, searchParams);
  }

  syncVariantGridState(state, container, searchParams);
  variantGridStateCache.set(cacheKey, state);

  return state;
}

function registerVariantGridSourceDocument(state, page, sourceDocument) {
  if (sourceDocument && page >= 1) {
    state.sourceDocuments.set(page, sourceDocument);
  }
}

function notifyVariantGridWaiters(state) {
  state.waiters.slice().forEach((waiter) => waiter());
}

function waitForVariantGridState(state, { targetVisibleCount = 0, requireFull = false } = {}) {
  return new Promise((resolve, reject) => {
    const maybeResolve = () => {
      if (state.error) {
        state.waiters = state.waiters.filter((waiter) => waiter !== maybeResolve);
        reject(state.error);
        return;
      }

      if (requireFull ? state.fullyHydrated : state.fullyHydrated || state.visibleRecords.length >= targetVisibleCount) {
        state.waiters = state.waiters.filter((waiter) => waiter !== maybeResolve);
        resolve(state);
      }
    };

    state.waiters.push(maybeResolve);
    maybeResolve();
  });
}

function inferFacetGroupKind(label, paramName) {
  const normalizedLabel = normalizeSearchValue(label);

  if (paramName.includes('filter.v.option.size')) return 'size';
  if (normalizedLabel === 'brand' || paramName.includes('filter.p.vendor')) return 'brand';
  if (normalizedLabel === 'gender' || paramName.includes('target-gender')) return 'gender';
  if (normalizedLabel === 'colour' || normalizedLabel === 'color') return 'colour';

  return 'generic';
}

function normalizeFacetValueForGroup(group, value) {
  if (group.kind === 'size') return normalizeSizeValue(value);
  if (group.kind === 'gender') return normalizeGenderValue(value);

  return normalizeSearchValue(value);
}

function extractFacetDefinitions(root = document) {
  const groups = new Map();

  root.querySelectorAll('[data-facet-group]').forEach((groupNode) => {
    const paramName = groupNode.dataset.facetParamName;

    if (!paramName) return;

    if (!groups.has(paramName)) {
      groups.set(paramName, {
        paramName,
        groupId: groupNode.dataset.facetGroupId || paramName,
        label: groupNode.dataset.facetLabel || paramName,
        operator: (groupNode.dataset.facetOperator || 'OR').toUpperCase(),
        kind: inferFacetGroupKind(groupNode.dataset.facetLabel || paramName, paramName),
        options: new Map(),
      });
    }

    const group = groups.get(paramName);

    groupNode.querySelectorAll('[data-facet-option]').forEach((optionNode) => {
      const rawValue = optionNode.dataset.facetRawValue;

      if (!rawValue) return;

      if (!group.options.has(rawValue)) {
        group.options.set(rawValue, {
          rawValue,
          label: optionNode.dataset.facetOptionLabel || rawValue,
          normalizedLabel: normalizeFacetValueForGroup(group, optionNode.dataset.facetOptionLabel || rawValue),
          originalCount: Number.parseInt(optionNode.dataset.facetOriginalCount || '0', 10) || 0,
          nodes: [],
        });
      }

      group.options.get(rawValue).nodes.push(optionNode);
    });
  });

  return groups;
}

function getActiveFacetSelections(searchParams, facetDefinitions) {
  const selections = new Map();
  const params = new URLSearchParams(searchParams);

  facetDefinitions.forEach((group, paramName) => {
    const rawValues = params.getAll(paramName);

    if (!rawValues.length) return;

    const labels = rawValues
      .map((rawValue) => group.options.get(rawValue)?.normalizedLabel)
      .filter(Boolean);

    selections.set(paramName, {
      rawValues,
      labels,
    });
  });

  return selections;
}

function removeFacetGroupFromSearchParams(searchParams, paramName) {
  const params = new URLSearchParams(getVariantGridBaseSearchParams(searchParams));
  params.delete(paramName);

  return FacetFiltersForm.normalizeSearchParams(params.toString());
}

function buildSearchParamsWithFacetOption(searchParams, paramName, rawValue) {
  const params = new URLSearchParams(getVariantGridBaseSearchParams(searchParams));
  params.delete(paramName);
  params.append(paramName, rawValue);

  return FacetFiltersForm.normalizeSearchParams(params.toString());
}

function getProductHandleFromItem(item) {
  const productLink = item.querySelector('a[href*="/products/"]');

  if (!productLink) return null;

  try {
    const linkedUrl = new URL(productLink.getAttribute('href'), window.location.origin);
    const handleMatch = linkedUrl.pathname.match(/^\/products\/([^/]+)/);

    return handleMatch ? handleMatch[1] : null;
  } catch (error) {
    return null;
  }
}

function buildAvailableOptionSets(productData) {
  const availableVariants = Array.isArray(productData?.variants)
    ? productData.variants.filter((variant) => variant.available !== false)
    : [];
  const availableOptionValues = new Set();
  const availableSizeValues = new Set();

  availableVariants.forEach((variant) => {
    (variant.options || []).forEach((optionValue) => {
      availableOptionValues.add(normalizeSearchValue(optionValue));
      availableSizeValues.add(normalizeSizeValue(optionValue));
    });
  });

  return { availableOptionValues, availableSizeValues };
}

function deriveFacetValuesForProduct(productData, facetDefinitions) {
  if (!productData) return {};

  const tagSet = new Set((productData.tags || []).map((tag) => normalizeSearchValue(tag)));
  const genderTagSet = new Set((productData.tags || []).map((tag) => normalizeGenderValue(tag)));
  const vendor = normalizeSearchValue(productData.vendor);
  const { availableOptionValues, availableSizeValues } = buildAvailableOptionSets(productData);
  const facetValues = {};

  facetDefinitions.forEach((group) => {
    const matches = [];

    group.options.forEach((option) => {
      let matched = false;

      if (group.kind === 'size') {
        matched = availableSizeValues.has(option.normalizedLabel);
      } else if (group.kind === 'brand') {
        matched = vendor === option.normalizedLabel || tagSet.has(option.normalizedLabel);
      } else if (group.kind === 'gender') {
        matched = genderTagSet.has(option.normalizedLabel);
      } else {
        matched = tagSet.has(option.normalizedLabel) || availableOptionValues.has(option.normalizedLabel);
      }

      if (matched) {
        matches.push(option.normalizedLabel);
      }
    });

    if (matches.length) {
      facetValues[group.paramName] = matches;
    }
  });

  return facetValues;
}

async function buildVisibleVariantGridRecord(item, facetDefinitions) {
  const linkedVariant = getLinkedVariantInfo(item);
  const handle = linkedVariant?.handle || getProductHandleFromItem(item);
  const productData = handle ? await fetchProductDataForHandle(handle) : null;
  const matchedVariant =
    linkedVariant && productData?.variants?.find((variant) => `${variant.id}` === linkedVariant.variantId);

  if (matchedVariant && matchedVariant.available === false) {
    return null;
  }

  return {
    html: item.outerHTML,
    handle: handle || '',
    linkedVariantId: linkedVariant?.variantId || '',
    facetValues: deriveFacetValuesForProduct(productData, facetDefinitions),
  };
}

async function getVariantGridPageDocument(state, page) {
  if (state.sourceDocuments.has(page)) {
    const sourceDocument = state.sourceDocuments.get(page);
    state.sourceDocuments.delete(page);
    return sourceDocument;
  }

  const pageHtml = await fetchSectionHtml(buildSectionUrl(state.pathname, state.sectionId, state.baseSearchParams, page));
  return parseHtmlDocument(pageHtml);
}

async function hydrateVariantGridPage(state, page) {
  const pageDocument = await getVariantGridPageDocument(state, page);
  const pageContainer = pageDocument.getElementById('ProductGridContainer');

  if (pageContainer) {
    state.totalRawPages = Number.parseInt(pageContainer.dataset.totalPages || `${state.totalRawPages || 1}`, 10) || 1;
    state.pageSize = Number.parseInt(pageContainer.dataset.pageSize || `${state.pageSize || 16}`, 10) || state.pageSize;
  }

  const list = pageDocument.querySelector('[data-product-grid-list]');
  const rawItems = list ? getVisibleGridItems(list) : [];
  const pageHandles = Array.from(
    new Set(
      rawItems
        .map((item) => getProductHandleFromItem(item))
        .filter(Boolean)
    )
  );

  await Promise.all(pageHandles.map((handle) => fetchProductDataForHandle(handle)));

  for (const item of rawItems) {
    const record = await buildVisibleVariantGridRecord(item, state.facetDefinitions);

    if (record) {
      state.visibleRecords.push(record);
    }
  }

  state.nextRawPage = page + 1;
  state.fullyHydrated = state.nextRawPage > state.totalRawPages;
  persistVariantGridState(state);
}

async function runVariantGridHydration(state) {
  try {
    while (!state.error) {
      const needsVisibleThreshold =
        !state.fullyHydrated && state.visibleRecords.length < Math.max(state.pendingVisibleCount, 0);
      const needsFinalization = !state.fullyHydrated && state.hydrateToEnd;

      if (!needsVisibleThreshold && !needsFinalization) break;

      await hydrateVariantGridPage(state, state.nextRawPage);
      notifyVariantGridWaiters(state);
    }
  } catch (error) {
    state.error = error;
  } finally {
    if (state.fullyHydrated) {
      state.hydrateToEnd = false;
    }

    state.hydrationPromise = null;
    notifyVariantGridWaiters(state);
    persistVariantGridState(state);
  }
}

function requestVariantGridHydration(
  state,
  { targetVisibleCount = 0, hydrateToEnd = false, sourceDocument = null, sourcePage = null } = {}
) {
  if (sourceDocument && sourcePage) {
    registerVariantGridSourceDocument(state, sourcePage, sourceDocument);
  }

  state.pendingVisibleCount = Math.max(state.pendingVisibleCount || 0, targetVisibleCount || 0);
  state.hydrateToEnd = state.hydrateToEnd || hydrateToEnd;

  if (!state.hydrationPromise) {
    state.hydrationPromise = runVariantGridHydration(state);
  }

  return waitForVariantGridState(state, { targetVisibleCount, requireFull: hydrateToEnd });
}

function matchesFacetSelection(group, recordValues = [], activeLabels = []) {
  if (!activeLabels.length) return true;

  if (group.operator === 'AND') {
    return activeLabels.every((activeLabel) => recordValues.includes(activeLabel));
  }

  return activeLabels.some((activeLabel) => recordValues.includes(activeLabel));
}

function recordMatchesActiveSelections(record, facetDefinitions, activeSelections, excludedParamName = '') {
  for (const [paramName, selection] of activeSelections.entries()) {
    if (paramName === excludedParamName) continue;

    const group = facetDefinitions.get(paramName);

    if (!group) continue;

    if (!Object.prototype.hasOwnProperty.call(record.facetValues || {}, paramName)) {
      continue;
    }

    const recordValues = record.facetValues?.[paramName] || [];

    if (!matchesFacetSelection(group, recordValues, selection.labels)) {
      return false;
    }
  }

  return true;
}

function buildFacetCountsForGroup(group, state, activeSelections, facetDefinitions) {
  const counts = new Map();

  group.options.forEach((option, rawValue) => {
    counts.set(rawValue, 0);
  });

  state.visibleRecords.forEach((record) => {
    if (!recordMatchesActiveSelections(record, facetDefinitions, activeSelections, group.paramName)) {
      return;
    }

    const recordValues = new Set(record.facetValues?.[group.paramName] || []);

    group.options.forEach((option, rawValue) => {
      if (recordValues.has(option.normalizedLabel)) {
        counts.set(rawValue, (counts.get(rawValue) || 0) + 1);
      }
    });
  });

  return counts;
}

function updateFacetOptionCount(option, nextCount, shouldCorrect) {
  option.nodes.forEach((node) => {
    const input = node.querySelector('input');
    const labelNode = node.querySelector('.facet-checkbox__text');
    const hiddenNode = node.querySelector('.visually-hidden');
    const nextValue = shouldCorrect ? nextCount : option.originalCount;

    if (labelNode) {
      labelNode.innerHTML = `<span class="facet-checkbox__text-label">${escapeHtml(option.label)}</span> (${nextValue})`;
    }

    if (hiddenNode) {
      hiddenNode.textContent = `${option.label} (${nextValue})`;
    }

    if (input && shouldCorrect) {
      const shouldDisable = nextValue === 0 && !input.checked;
      input.disabled = shouldDisable;
      node.classList.toggle('disabled', shouldDisable);
      node.querySelectorAll('label, .facets__label').forEach((label) => {
        label.classList.toggle('disabled', shouldDisable);
      });
    }

    node.dataset.correctedCount = `${nextValue}`;
  });
}

function renderVariantGridPage(state, requestedPage, metadata, { animate = false, final = false } = {}) {
  const container = document.getElementById('ProductGridContainer');

  if (!container) return { normalizedPage: requestedPage, totalVisiblePages: 0 };

  const totalVisiblePages = state.visibleRecords.length > 0 ? Math.ceil(state.visibleRecords.length / state.pageSize) : 0;
  const normalizedPage =
    totalVisiblePages === 0 ? 1 : Math.min(Math.max(requestedPage, 1), final ? totalVisiblePages : Math.max(requestedPage, 1));
  const pageStart = (normalizedPage - 1) * state.pageSize;
  const pageRecords = state.visibleRecords.slice(pageStart, pageStart + state.pageSize);
  const emptyStateTemplate = container.querySelector('[data-empty-state-template]');
  const emptyStateNode = emptyStateTemplate?.content?.firstElementChild?.cloneNode(true);

  container.dataset.currentPage = `${normalizedPage}`;
  container.dataset.totalPages = `${Math.max(totalVisiblePages, 1)}`;

  if (!pageRecords.length) {
    if (emptyStateNode) {
      container.innerHTML = '';
      container.appendChild(emptyStateNode);
    }

    return { normalizedPage, totalVisiblePages };
  }

  const collectionElement = container.querySelector('.collection');
  const productList = container.querySelector('[data-product-grid-list]');

  if (!collectionElement || !productList) {
    return { normalizedPage, totalVisiblePages };
  }

  productList.innerHTML = pageRecords.map((record) => record.html).join('');

  productList.querySelectorAll('.grid__item').forEach((item, index) => {
    if (item.classList.contains('scroll-trigger')) {
      item.style.setProperty('--animation-order', `${index + 1}`);
    }
  });

  const existingPagination = collectionElement.querySelector('.pagination-wrapper');

  if (final) {
    const paginationMarkup = buildVirtualPaginationMarkup(
      totalVisiblePages,
      normalizedPage,
      state.pathname,
      state.baseSearchParams
    );

    if (paginationMarkup) {
      const nextPagination = createElementFromHtml(paginationMarkup);

      if (existingPagination) {
        existingPagination.replaceWith(nextPagination);
      } else {
        collectionElement.appendChild(nextPagination);
      }
    } else if (existingPagination) {
      existingPagination.remove();
    }
  } else if (existingPagination) {
    existingPagination.remove();
  }

  if (animate) {
    animateVirtualGrid(productList);
  }

  if (final && metadata) {
    const correctedMetadata = buildCorrectedCountMetadata(metadata, state.visibleRecords.length);
    const countText = buildCountText(correctedMetadata, state.visibleRecords.length);

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const target = document.getElementById(id);

      if (!target) return;

      target.innerHTML = countText;
      applyCountMetadata(target, correctedMetadata, state.visibleRecords.length);
    });

    document.querySelectorAll('.mobile-facets__count').forEach((target) => {
      target.innerHTML = countText;
    });
  }

  return { normalizedPage, totalVisiblePages };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const rawSearchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      const searchParams = FacetFiltersForm.normalizeSearchParams(rawSearchParams);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      if (rawSearchParams !== searchParams) {
        FacetFiltersForm.renderPage(searchParams, null, true, true);
        return;
      }

      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    const onPaginationClick = (event) => {
      const paginationLink = event.target.closest('.pagination a[href]');
      const container = document.getElementById('ProductGridContainer');

      if (!paginationLink || !container || !shouldUseVariantAwarePagination(container)) return;

      const nextUrl = new URL(paginationLink.href, window.location.origin);

      if (nextUrl.origin !== window.location.origin || nextUrl.pathname !== window.location.pathname) return;
      if (!hasVariantOptionFilter(nextUrl.search)) return;

      event.preventDefault();
      FacetFiltersForm.renderPage(nextUrl.search.slice(1), null, true);
    };

    window.addEventListener('popstate', onHistoryChange);
    document.addEventListener('click', onPaginationClick);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static normalizeSearchParams(rawSearchParams = '') {
    const params = new URLSearchParams(rawSearchParams);

    if (!params.has('sort_by')) {
      params.set('sort_by', 'created-descending');
    }

    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true, replaceURL = false) {
    const normalizedSearchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    const previousSearchParams = FacetFiltersForm.searchParamsPrev;
    const previousBaseSearchParams = getVariantGridBaseSearchParams(previousSearchParams);
    const nextBaseSearchParams = getVariantGridBaseSearchParams(normalizedSearchParams);
    const container = document.getElementById('ProductGridContainer');
    FacetFiltersForm.searchParamsPrev = normalizedSearchParams;
    FacetFiltersForm.renderToken += 1;
    const requestToken = FacetFiltersForm.renderToken;

    if (
      container &&
      shouldUseVariantAwarePagination(container, `?${normalizedSearchParams}`) &&
      previousBaseSearchParams === nextBaseSearchParams
    ) {
      FacetFiltersForm.renderVariantAwareRoute(normalizedSearchParams, requestToken).catch(() => {
        if (requestToken === FacetFiltersForm.renderToken) {
          FacetFiltersForm.setLoadingState(false);
        }
      });

      if (updateURLHash) FacetFiltersForm.updateURLHash(normalizedSearchParams, replaceURL);
      return;
    }

    const sections = FacetFiltersForm.getSections();
    FacetFiltersForm.setLoadingState(true);

    sections.forEach((section) => {
      const url = buildSectionUrl(
        window.location.pathname,
        section.section,
        normalizedSearchParams,
        getRequestedPage(`?${normalizedSearchParams}`)
      );
      FacetFiltersForm.renderSection(url, event, requestToken);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(normalizedSearchParams, replaceURL);
  }

  static async renderSection(url, event, requestToken) {
    try {
      const html = await fetchSectionHtml(url);

      if (requestToken !== FacetFiltersForm.renderToken) return;

      const parsedHTML = parseHtmlDocument(html);
      FacetFiltersForm.renderFilters(parsedHTML, event);
      await FacetFiltersForm.renderProductGridContainer(parsedHTML, requestToken);
    } catch (error) {
      if (requestToken === FacetFiltersForm.renderToken) {
        console.warn('Facet section render failed', error);
        FacetFiltersForm.setLoadingState(false);
      }
    }
  }

  static async renderProductGridContainer(parsedHTML, requestToken) {
    if (requestToken !== FacetFiltersForm.renderToken) return;

    const sourceContainer = parsedHTML.getElementById('ProductGridContainer');
    const targetContainer = document.getElementById('ProductGridContainer');

    if (!sourceContainer || !targetContainer) return;

    Array.from(targetContainer.attributes).forEach((attribute) => {
      if (attribute.name !== 'id') {
        targetContainer.removeAttribute(attribute.name);
      }
    });

    Array.from(sourceContainer.attributes).forEach((attribute) => {
      if (attribute.name !== 'id') {
        targetContainer.setAttribute(attribute.name, attribute.value);
      }
    });

    targetContainer.innerHTML = sourceContainer.innerHTML;

    if (requestToken !== FacetFiltersForm.renderToken) return;

    FacetFiltersForm.setLoadingState(true);

    targetContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    const container = targetContainer;

    if (shouldUseVariantAwarePagination(container, `?${FacetFiltersForm.searchParamsPrev}`)) {
      try {
        await FacetFiltersForm.renderVariantAwareRoute(FacetFiltersForm.searchParamsPrev, requestToken, parsedHTML);
      } catch (error) {
        console.warn('Variant-aware grid fallback engaged', error);
        FacetFiltersForm.renderProductCount(parsedHTML);
      }
    } else {
      FacetFiltersForm.renderProductCount(parsedHTML);
    }

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer').innerHTML);
    }
  }

  static setLoadingState(nextState) {
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    const gridLoading = typeof nextState === 'boolean' ? nextState : Boolean(nextState?.grid);
    const countsLoading = typeof nextState === 'boolean' ? nextState : Boolean(nextState?.counts);

    loadingSpinners.forEach((spinner) => spinner.classList.toggle('hidden', !countsLoading));

    const collectionElement = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collectionElement) {
      collectionElement.classList.toggle('loading', gridLoading);
    }

    if (countContainer) {
      countContainer.classList.toggle('loading', countsLoading);
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.toggle('loading', countsLoading);
    }
  }

  static renderProductCount(sourceDocument) {
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const target = document.getElementById(id);
      const source = sourceDocument.getElementById(id);

      if (!target || !source) return;

      target.innerHTML = source.innerHTML;

      countMetadataAttributes.forEach((attribute) => {
        if (source.hasAttribute(attribute)) {
          target.setAttribute(attribute, source.getAttribute(attribute));
        } else {
          target.removeAttribute(attribute);
        }
      });
    });

    const sourceMobileCount = sourceDocument.querySelector('.mobile-facets__count');
    document.querySelectorAll('.mobile-facets__count').forEach((target) => {
      if (sourceMobileCount) {
        target.innerHTML = sourceMobileCount.innerHTML;
      }
    });

    FacetFiltersForm.setLoadingState(false);
  }

  static async renderVariantAwareRoute(searchParams, requestToken, sourceDocument = null) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const metadata = extractCountMetadata(sourceDocument || document) || extractCountMetadata(document);
    const requestedPage = getRequestedPage(`?${searchParams}`);
    const state = getVariantGridState(container, searchParams);
    const facetDefinitions = extractFacetDefinitions(document);
    const hadRequestedPage = state.fullyHydrated || state.visibleRecords.length >= requestedPage * state.pageSize;

    state.facetDefinitions = facetDefinitions;
    state.error = null;

    FacetFiltersForm.setLoadingState({ grid: !hadRequestedPage, counts: !state.fullyHydrated });

    await requestVariantGridHydration(state, {
      targetVisibleCount: requestedPage * state.pageSize,
      sourceDocument,
      sourcePage: requestedPage,
    });

    if (requestToken !== FacetFiltersForm.renderToken) return;

    const { normalizedPage } = renderVariantGridPage(state, requestedPage, metadata, {
      animate: !hadRequestedPage,
      final: state.fullyHydrated,
    });

    if (state.fullyHydrated) {
      if (normalizedPage !== requestedPage) {
        const normalizedSearchParams = buildPageSearchParams(searchParams, normalizedPage);
        FacetFiltersForm.searchParamsPrev = normalizedSearchParams;
        FacetFiltersForm.updateURLHash(normalizedSearchParams, true);
      }

      FacetFiltersForm.setLoadingState(false);
      return;
    }

    FacetFiltersForm.setLoadingState({ grid: false, counts: true });

    requestVariantGridHydration(state, {
      hydrateToEnd: true,
      sourceDocument,
      sourcePage: requestedPage,
    })
      .then(async (finalState) => {
        if (requestToken !== FacetFiltersForm.renderToken) return;
        if (getVariantGridBaseSearchParams(searchParams) !== getVariantGridBaseSearchParams(FacetFiltersForm.searchParamsPrev)) {
          return;
        }

        const activeSearchParams = FacetFiltersForm.searchParamsPrev;
        const activeRequestedPage = getRequestedPage(`?${activeSearchParams}`);
        const finalRender = renderVariantGridPage(finalState, activeRequestedPage, metadata, {
          animate: false,
          final: true,
        });

        if (finalRender.normalizedPage !== activeRequestedPage) {
          const normalizedSearchParams = buildPageSearchParams(activeSearchParams, finalRender.normalizedPage);
          FacetFiltersForm.searchParamsPrev = normalizedSearchParams;
          FacetFiltersForm.updateURLHash(normalizedSearchParams, true);
        }

        FacetFiltersForm.setLoadingState(false);
      })
      .catch((error) => {
        if (requestToken === FacetFiltersForm.renderToken) {
          console.warn('Variant-aware grid finalization failed', error);
          FacetFiltersForm.setLoadingState(false);
        }
      });
  }

  static renderFilters(html, event) {
    const parsedHTML = html?.nodeType === Node.DOCUMENT_NODE ? html : new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams, replaceURL = false) {
    const normalizedSearchParams = FacetFiltersForm.normalizeSearchParams(searchParams);
    const nextUrl = `${window.location.pathname}${normalizedSearchParams && '?'.concat(normalizedSearchParams)}`;
    const state = { searchParams: normalizedSearchParams };

    if (replaceURL) {
      history.replaceState(state, '', nextUrl);
      return;
    }

    history.pushState(state, '', nextUrl);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.renderToken = 0;
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.normalizeSearchParams(window.location.search.slice(1));
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

if (window.location.search.slice(1) !== FacetFiltersForm.searchParamsInitial) {
  if (document.getElementById('ProductGridContainer') && document.getElementById('product-grid')) {
    FacetFiltersForm.renderPage(FacetFiltersForm.searchParamsInitial, null, true, true);
  } else {
    FacetFiltersForm.updateURLHash(FacetFiltersForm.searchParamsInitial, true);
  }
}

if (document.getElementById('ProductGridContainer') && document.getElementById('product-grid')) {
  const initialContainer = document.getElementById('ProductGridContainer');
  if (shouldUseVariantAwarePagination(initialContainer)) {
    FacetFiltersForm.renderToken += 1;
    FacetFiltersForm.renderVariantAwareRoute(FacetFiltersForm.searchParamsPrev, FacetFiltersForm.renderToken, document).catch(() => {
      FacetFiltersForm.renderProductCount(document);
    });
  }
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
